import { maxAttachmentCount } from "api/constants";
import { EAttachmentType } from "api/enums";
import AttachmentModel from "api/models/AttachmentModel";
import AttachmentService from "api/services/AttachmentService";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import { ContractStore, MessageStore } from "..";

class Store {
  public attachments: AttachmentModel[] = [];
  private attachmentsToExclude: AttachmentModel[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public reset() {
    this.attachments = [];
    this.attachmentsToExclude = [];
  }

  public hasFileBeingUploaded() {
    return this.attachments.some((att) => att.isUploading());
  }

  public validadeAttachmentsCount(files: File[]) {
    return files.length + this.attachments.length <= maxAttachmentCount;
  }

  public async uploadAdditiveAttachment(file: File) {
    try {
      const contractId = ContractStore.getCurrentContract()?.uid;
      if (!contractId) return;

      await AttachmentService.validateAttachment(file);

      const attachment = AttachmentService.uploadAttachmentToStorage(
        contractId,
        file,
        EAttachmentType.MOVIMENTS,
        () => {
          MessageStore.error(lang("attachments.errors.uploadError"));
        }
      );
      this.addAttachmentToList(attachment);
    } catch (error: any) {
      MessageStore.error(error.message);
    }
  }

  public async removeAllUnsavedAdditiveAttachment() {
    const promises: Promise<void>[] = [];
    const attachments = [...this.attachments];
    for (const attachment of attachments) {
      if (!attachment.isSaved()) {
        promises.push(this.removeAdditiveAttachment(attachment));
      }
    }
    await Promise.all(promises);
  }

  public async removeAdditiveAttachment(attachment: AttachmentModel) {
    const contractId = ContractStore.getCurrentContract()?.uid;
    if (!contractId) return;

    if (attachment.isSaved()) {
      this.removeAttachmentFromList(attachment);
      this.attachmentsToExclude.push(attachment);
      return;
    }

    if (attachment.isUploading()) {
      attachment.cancelUpload();
      this.removeAttachmentFromList(attachment);
      return;
    }

    return AttachmentService.removeAttachmentFromStorage(contractId, attachment)
      .then(() => {
        this.removeAttachmentFromList(attachment);
      })
      .catch(() => {
        MessageStore.error(lang("attachments.errors.removeError"));
      });
  }

  public async saveAdditiveAttachments(): Promise<string[]> {
    let contract = ContractStore.getCurrentContract();
    if (!contract) return [];

    const attachments = this.attachments.filter((att) =>
      att.isSuccessfullyUploaded()
    );
    if (!attachments.length) return [];

    return AttachmentService.applyAttachments(
      contract,
      attachments,
      this.attachmentsToExclude,
      () => Promise.resolve()
    );
  }

  private addAttachmentToList(attachment: AttachmentModel) {
    this.attachments.push(makeAutoObservable(attachment));
  }

  private removeAttachmentFromList(attachment: AttachmentModel) {
    const index = this.attachments.indexOf(attachment);
    if (index !== -1) {
      this.attachments.splice(index, 1);
    }
  }
}

const AdditiveAttachmentStore = new Store();
export default AdditiveAttachmentStore;
